import React, { createContext, ReactNode, useContext, useEffect, useMemo, useState } from 'react';
import {
  BehaviorDetector,
  Click,
  Environment,
  Keyboard,
  Mouse,
  Resize,
  Scroll,
  Tap,
  Timing,
  Visibility,
} from '@axeptio/behavior-detection';

export interface ClientMetricsType {
  score: number | null;
  isCompliant: boolean;
}

interface ClientMetricsProviderProps {
  children: ReactNode;
}

export const ClientMetricsContext = createContext<ClientMetricsType | undefined>(undefined);

export function ClientMetricsProvider({ children }: ClientMetricsProviderProps) {
  const [score, setScore] = useState<number | null>(null);
  const [isCompliant, setIsCompliant] = useState<boolean>(true);

  useEffect(() => {
    const detector = new BehaviorDetector()
      .addStrategy(new Mouse())
      .addStrategy(new Click())
      .addStrategy(new Scroll())
      .addStrategy(new Keyboard())
      .addStrategy(new Tap())
      .addStrategy(new Resize())
      .addStrategy(new Visibility())
      .addStrategy(new Environment())
      .addStrategy(new Timing());

    detector.start();

    const interval = setInterval(async () => {
      try {
        const result = await detector.score();
        setScore(result.score);
        setIsCompliant(result.score > 0.4);
      } catch (error) {
        // error
      }
    }, 4000);

    return () => {
      clearInterval(interval);
      detector.stop();
    };
  }, []);

  const contextValue = useMemo(() => ({ score, isCompliant }), [score, isCompliant]);

  return <ClientMetricsContext.Provider value={contextValue}>{children}</ClientMetricsContext.Provider>;
}

export const useClientMetrics = (): ClientMetricsType => {
  const context = useContext(ClientMetricsContext);

  if (context === undefined) {
    throw new Error('useClientMetrics must be used within a ClientMetricsProvider');
  }

  return context;
};
